import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Card,
  CardHeader,
  CardBody,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Flex,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Grid,
  GridItem,
  Tag,
  TagLabel,
  IconButton,
  useDisclosure,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Link,
} from '@chakra-ui/react';
import { DownloadIcon, ViewIcon, WarningIcon, InfoIcon, ExternalLinkIcon, CheckCircleIcon } from '@chakra-ui/icons';

import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';
import { AppContext } from './AppContext';
import { useFeatureFlags } from './FeatureFlags';
import ModernChat from './ModernChat';
import Cms1500Replica from './mph-sa/Cms1500Replica';
import VarianceDocumentViewer from './variance/VarianceDocumentViewer';
import CaseFeedbackSection from './CaseFeedbackSection';

const MPHSurgicalAssistAnalysis = () => {
  const { caseId } = useParams();
  const { accessToken } = useContext(AppContext);
  const { featureFlags } = useFeatureFlags();
  const skipFetchRef = useRef(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [claimsData, setClaimsData] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [error, setError] = useState(null);

  
  const [loadingMode, setLoadingMode] = useState(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [pipelineVersion, setPipelineVersion] = useState('advanced');

  const [results, setResults] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const chatRef = useRef(null);

  const handleSendFeedbackToChat = (feedbackText) => {
    setTabIndex(1); // Switch to Chat tab
    setTimeout(() => {
      if (chatRef.current) {
        chatRef.current.sendMessage(`Please review this feedback:\n\n${feedbackText}`);
      }
    }, 100); // Small delay to ensure tab is rendered
  };


  const handleGeneratePdf = async () => {
    if (!caseId) {
      setError('Case ID is missing.');
      return;
    }

    setIsPdfGenerating(true);
    setError(null);
    try {
      const response = await callApi(`/mph_sa/cases/${caseId}/generate-report`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_id: 'mph_pre_90_full',
          claim_type: claimType,
          pipeline_version: pipelineVersion
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const newTab = window.open(url, '_blank');

        if (newTab) {
          newTab.focus();
        } else {
          setError('Your browser blocked the new tab. Please allow pop-ups for this site to view the PDF.');
        }
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate PDF.');
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError(err.message);
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const runAnalysis = async (mode) => {

    skipFetchRef.current = true;
    setPipelineVersion(mode);
    setLoadingMode(mode);
    setError(null);
    try {
      // 1. Try to fetch existing results first
      const getResponse = await callApi(`/mph_sa/analyze_case/${caseId}?claim_type=${claimType}&pipeline_version=${mode}`, accessToken, {
        method: 'GET'
      });
      
      let needsGeneration = true;
      if (getResponse.ok) {
        const getData = await getResponse.json();
        if (getData.status === 'success' && getData.results) {
          setResults(getData.results);
          if (getData.claims) {
            setClaimsData(getData.claims);
          }
          needsGeneration = false;
        }
      }

      // 2. If no existing results, generate new ones
      if (needsGeneration) {
        const response = await callApi('/mph_sa/analyze_case', accessToken, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            external_case_id: caseId,
            claim_type: claimType,
            pipeline_version: mode,
            clean_phi: true,
            allow_noop: caseType === 'MPH Surgical Assist NoOp'
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'success') {
            setResults(data.results);
          } else {
            setError(data.message || 'An error occurred during analysis.');
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          setError(errorData.message || `Server error: ${response.status}`);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to connect to the server.');
    } finally {
      setLoadingMode(null);
    }
  };

  const claimType = 'standard'; // Hardcode to standard for Surgical Assist
  
  // Document Viewer State
  const [caseFiles, setCaseFiles] = useState([]);
  const [caseType, setCaseType] = useState(null);

  useEffect(() => {
    const fetchExistingResults = async () => {
      if (skipFetchRef.current) {
        skipFetchRef.current = false;
        return;
      }
      setInitialLoading(true);
      setError(null);
      try {
        // Fetch case files for document viewer and check case type
        const detailsResponse = await callApi(`/get_case_details/${caseId}`, accessToken);
        if (detailsResponse.ok) {
          const detailsData = await detailsResponse.json();
          setCaseFiles(detailsData.files || []);
          setCaseType(detailsData.case_type);
          
          // Redirect if this is an IONM case
          if (detailsData.case_type === 'IONM Modern') {
            window.location.href = `/ionm-strict-analysis/${caseId}`;
            return;
          }

          // Extract AI Analysis metadata
          if (detailsData.coder_score || detailsData.coder_reasoning) {
            setAiAnalysis({
              score: detailsData.coder_score,
              reasoning: detailsData.coder_reasoning,
              notes: detailsData.coder_notes,
              instructions: detailsData.coder_instructions
            });
          }

          // Set claims data directly from the case details if available
          if (detailsData.cpt_coding_results) {
            const cptObj = detailsData.cpt_coding_results;
            let cptArray = [];
            
            // Handle both array format and dictionary format
            if (Array.isArray(cptObj)) {
              cptArray = cptObj;
            } else if (cptObj.coding_results_list) {
              cptArray = cptObj.coding_results_list;
            } else {
              // It's a dictionary of codes (e.g., {"61512": {...}, "61781": {...}})
              cptArray = Object.values(cptObj).filter(item => item && typeof item === 'object' && item.cpt_code);
            }

            setClaimsData({
              'standard': {
                cpt_codes: cptArray,
                original_cpt_codes: cptArray,
                adjustment_log: []
              }
            });
          }
        }

        // Fetch existing SA analysis results (ICD-10, etc.)
        const analysisResponse = await callApi(`/mph_sa/analyze_case/${caseId}?claim_type=${claimType}&pipeline_version=${pipelineVersion}`, accessToken, {
          method: 'GET'
        });
        if (analysisResponse.ok) {
          const analysisData = await analysisResponse.json();
          if (analysisData.status === 'success' && analysisData.results) {
            setResults(analysisData.results);
          }
        }

      } catch (err) {
        console.error("Error fetching existing results:", err);
        setError("Failed to load case details.");
      } finally {
        setInitialLoading(false);
      }
    };

    if (caseId && accessToken) {
      fetchExistingResults();
    }
  }, [caseId, accessToken]);

  

  return (
    <Box>
      <HeaderMenus />
      <Box p={8} maxWidth="1200px" margin="0 auto">
        <HStack justifyContent="space-between" mb={6}>
          <VStack align="start" spacing={1}>
            <HStack spacing={4}>
              <Heading size="lg">Surgical Assist Analysis</Heading>
              <Button 
                as={RouterLink} 
                to="/mph-guidelines" 
                size="sm" 
                colorScheme="blue" 
                variant="outline"
                leftIcon={<InfoIcon />}
              >
                View Coding Guidelines
              </Button>
            </HStack>
          </VStack>


          <HStack spacing={4}>
            <Button
              as={RouterLink}
              to={`/mph-sa-compare-v2/${caseId}`}
              size="md"
              colorScheme="purple"
              variant="outline"
            >
              Compare V1 vs V2
            </Button>
            <Button 
              colorScheme="teal" 
              leftIcon={<DownloadIcon />}
              onClick={handleGeneratePdf}
              isLoading={isPdfGenerating}
              loadingText="Generating..."
              isDisabled={!results}
            >
              Generate PDF
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={() => runAnalysis('advanced')}

              isLoading={loadingMode === 'advanced'}
              loadingText="Running..."
            >
              Run SA Analysis
            </Button>
            <Badge colorScheme="purple" p={2} borderRadius="md">
              Surgical Assist (Standard Claim)
            </Badge>
          </HStack>

        </HStack>

        <Text mb={6} color="gray.600">Case ID: {caseId}</Text>

        {/* Alert Notice for No-Op Case */}
        {caseType === 'MPH Surgical Assist NoOp' && (
          <Alert status="success" variant="subtle" mb={8} borderRadius="md" border="1px solid" borderColor="green.300" bg="green.50">
            <AlertIcon boxSize="24px" mr={4} />
            <Box flex="1">
              <AlertTitle fontSize="lg" mb={1} color="green.800">
                No-Op Case
              </AlertTitle>
              <AlertDescription display="block" color="green.800">
                This case was processed using the No-Op pipeline because it is missing a standard Primary Operative Report.
              </AlertDescription>
            </Box>
          </Alert>
        )}

        {/* Alert Notice for Low Confidence Score */}
        {results && results.overall_confidence_score !== undefined && results.overall_confidence_score < 8 && (
          <Alert status="error" variant="subtle" mb={8} borderRadius="md" border="1px solid" borderColor="red.300" bg="red.50">
            <AlertIcon boxSize="24px" mr={4} />
            <Box flex="1">
              <AlertTitle fontSize="lg" mb={1} color="red.800">
                Alert: Needs Review (Score: {results.overall_confidence_score}/10)
              </AlertTitle>
              <AlertDescription display="block" color="red.800">
                <Text fontWeight="bold" display="inline">Notes: </Text>
                {results.overall_gut_check_notes || 'No notes provided.'}
              </AlertDescription>
            </Box>
          </Alert>
        )}

        {/* Feedback Section */}
        <Box mb={8}>
          <CaseFeedbackSection 
            caseId={caseId} 
            feedbackType="mph_surgical_assist"
            contextData={{
              pipeline_version: pipelineVersion,
              claim_type: claimType,
              has_claims_data: !!claimsData,
              has_analysis_data: !!results
            }}
            accessToken={accessToken} 
            onSendToChat={handleSendFeedbackToChat}
          />
        </Box>

        {error && (
          <Alert status="error" mb={6} borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Box>
          </Alert>
        )}

        <Tabs variant="enclosed" colorScheme="blue" index={tabIndex} onChange={(index) => setTabIndex(index)}>
          <TabList>
            <Tab>Surgical Assist Analysis</Tab>
            <Tab>Coding Chat</Tab>
          </TabList>

          <TabPanels>
            
            <TabPanel px={0} py={4}>
              {initialLoading && (
                <VStack py={12} spacing={4}>
                  <Spinner size="xl" color="blue.500" thickness="4px" />
                  <Text>Loading case details...</Text>
                </VStack>
              )}

              {loadingMode && !results && !initialLoading && (
                <VStack py={12} spacing={4}>
                  <Spinner size="xl" color="blue.500" thickness="4px" />
                  <Text>Running Surgical Assist analysis and scoring...</Text>
                </VStack>
              )}

              {results && (
                <VStack spacing={6} align="stretch" mb={6}>
                  
                  {/* Top Level Context */}
                  <HStack spacing={4} mb={2}>
                    <Badge colorScheme="blue" fontSize="md" p={2} borderRadius="md">
                      Payer: {results.context?.payer || 'Unknown'}
                    </Badge>
                    <Badge colorScheme="purple" fontSize="md" p={2} borderRadius="md">
                      Carrier: {results.context?.carrier || 'Unknown'}
                    </Badge>
                  </HStack>

                  {/* CMS-1500 Replica */}
                  <Cms1500Replica results={results} claimsData={claimsData} claimType={claimType} />

                  {/* Ordered Diagnoses & Invalid Diagnoses */}
                  <Card mt={4}>
                    <CardBody>
                      {results.ordered_diagnoses && results.ordered_diagnoses.length > 0 ? (
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>Pointer</Th>
                              <Th>Code</Th>
                              <Th>Description</Th>
                              <Th>Classification</Th>
                              <Th>Policy Match</Th>
                              <Th isNumeric>Score</Th>
                              <Th>Rationale</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {results.ordered_diagnoses.map((dx, idx) => (
                              <Tr key={idx} bg={dx.needs_review ? "yellow.50" : "transparent"}>
                                <Td fontWeight="bold">{dx.pointer}</Td>
                                <Td fontWeight="bold">
                                  {dx.code}
                                  {dx.needs_review && (
                                    <WarningIcon color="yellow.500" ml={2} boxSize={3} title="Requires review" />
                                  )}
                                </Td>
                                <Td>
                                  <Text>{dx.description}</Text>
                                  {dx.evidence && (
                                    <Box mt={2} p={2} bg="gray.50" borderRadius="md" borderLeft="2px solid" borderColor="blue.400">
                                      <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>Evidence Found:</Text>
                                      <Text fontSize="xs" color="gray.600" whiteSpace="pre-wrap" fontFamily="monospace">
                                        {dx.evidence}
                                      </Text>
                                    </Box>
                                  )}
                                </Td>
                                <Td>
                                  <Badge colorScheme="blue">
                                    {dx.classification ? dx.classification.replace('_', ' ') : 'diagnosis'}
                                  </Badge>
                                </Td>
                                <Td>
                                  {dx.policy_match ? (
                                    <Badge colorScheme="green">Yes</Badge>
                                  ) : (
                                    <Text color="gray.400" fontSize="sm">No</Text>
                                  )}
                                </Td>
                                <Td isNumeric fontWeight="bold">{dx.score}</Td>
                                <Td fontSize="sm">{dx.rationale}</Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      ) : (
                        <Text color="gray.500">No valid diagnoses found.</Text>
                      )}
                    </CardBody>
                  </Card>

                  

                  {/* Policy Discovery Tracking */}
                  {/* {results.policy_tracking && Object.keys(results.policy_tracking).length > 0 && (
                    <Card>
                      <CardHeader>
                        <Heading size="md">Policy Discovery by CPT</Heading>
                      </CardHeader>
                      <CardBody>
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>CPT Code</Th>
                              <Th>NCDs Found</Th>
                              <Th>LCDs Found</Th>
                              <Th>Articles Found</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {Object.entries(results.policy_tracking).map(([cpt, tracking]) => (
                              <Tr key={cpt}>
                                <Td fontWeight="bold">{cpt}</Td>
                                <Td>
                                  {tracking.NCD && tracking.NCD.length > 0 ? (
                                    tracking.NCD.map(id => <Badge key={id} colorScheme="green" mr={1}>{id}</Badge>)
                                  ) : (
                                    <Text color="gray.400" fontSize="xs">None</Text>
                                  )}
                                </Td>
                                <Td>
                                  {tracking.LCD && tracking.LCD.length > 0 ? (
                                    tracking.LCD.map(id => <Badge key={id} colorScheme="blue" mr={1}>{id}</Badge>)
                                  ) : (
                                    <Text color="gray.400" fontSize="xs">None</Text>
                                  )}
                                </Td>
                                <Td>
                                  {tracking.Article && tracking.Article.length > 0 ? (
                                    tracking.Article.map(id => <Badge key={id} colorScheme="purple" mr={1}>{id}</Badge>)
                                  ) : (
                                    <Text color="gray.400" fontSize="xs">None</Text>
                                  )}
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </CardBody>
                    </Card>
                  )} */}

                  {/* CPT Code Evidence Card */}
                  {claimsData && claimsData[claimType] && (
                    <Accordion allowToggle defaultIndex={[0]} mt={4}>
                      <AccordionItem border="none">
                        <Card borderLeft="4px solid" borderColor="teal.400">
                          <CardHeader p={0}>
                            <AccordionButton _hover={{ bg: 'teal.50' }} p={4}>
                              <Box flex="1" textAlign="left">
                                <Heading size="md" color="teal.700">CPT Code Evidence</Heading>
                              </Box>
                              <AccordionIcon />
                            </AccordionButton>
                          </CardHeader>
                          <AccordionPanel pb={4}>
                            <CardBody pt={0}>
                              <VStack align="stretch" spacing={6}>
                                {/* Original Codes Table */}
                                {claimsData[claimType].original_cpt_codes && claimsData[claimType].original_cpt_codes.length > 0 && (
                                  <Box>
                                    <Heading size="sm" mb={2}>Original CPT Codes</Heading>
                                    <Table variant="simple" size="sm">
                                      <Thead>
                                        <Tr>
                                          <Th>Code</Th>
                                          <Th>Units</Th>
                                          <Th>Modifiers</Th>
                                          <Th>Status</Th>
                                          <Th>Justification</Th>
                                        </Tr>
                                      </Thead>
                                      <Tbody>
                                        {claimsData[claimType].original_cpt_codes.map((code, idx) => (
                                          <Tr key={idx}>
                                            <Td fontWeight="bold">{code.cpt_code}</Td>
                                            <Td>{code.units}</Td>
                                            <Td>{code.modifiers ? (Array.isArray(code.modifiers) ? code.modifiers.join(', ') : code.modifiers) : (code.modifier || '-')}</Td>
                                            <Td>
                                              <Badge colorScheme={code.status === 'final' ? 'green' : 'yellow'}>
                                                {code.status || 'original'}
                                              </Badge>
                                            </Td>
                                            <Td fontSize="xs" whiteSpace="normal">{code.justification}</Td>
                                          </Tr>
                                        ))}
                                      </Tbody>
                                    </Table>
                                  </Box>
                                )}

                                {/* Adjusted Codes Table */}
                                <Box>
                                  <Heading size="sm" mb={2}>Adjusted CPT Codes</Heading>
                                  <Table variant="simple" size="sm">
                                    <Thead>
                                      <Tr>
                                        <Th>Code</Th>
                                        <Th>Units</Th>
                                        <Th>Modifiers</Th>
                                        <Th>Status</Th>
                                        <Th>Justification</Th>
                                      </Tr>
                                    </Thead>
                                    <Tbody>
                                      {claimsData[claimType].cpt_codes && claimsData[claimType].cpt_codes.map((code, idx) => (
                                        <Tr key={idx}>
                                          <Td fontWeight="bold">{code.cpt_code}</Td>
                                          <Td>{code.units}</Td>
                                          <Td>{code.modifiers ? (Array.isArray(code.modifiers) ? code.modifiers.join(', ') : code.modifiers) : (code.modifier || '-')}</Td>
                                          <Td>
                                            <Badge colorScheme={code.status === 'final' ? 'green' : 'yellow'}>
                                              {code.status || 'final'}
                                            </Badge>
                                          </Td>
                                          <Td fontSize="xs" whiteSpace="normal">{code.justification}</Td>
                                        </Tr>
                                      ))}
                                    </Tbody>
                                  </Table>
                                </Box>

                                {/* Adjustment Log */}
                                {claimsData[claimType].adjustment_log && claimsData[claimType].adjustment_log.length > 0 && (
                                  <Box>
                                    <Heading size="sm" mb={2}>Adjustment Log</Heading>
                                    <VStack align="start" spacing={2}>
                                      {claimsData[claimType].adjustment_log.map((log, idx) => (
                                        <HStack key={idx} align="start">
                                          <InfoIcon color="blue.400" mt={1} />
                                          <Text fontSize="sm">{typeof log === 'string' ? log : JSON.stringify(log)}</Text>
                                        </HStack>
                                      ))}
                                    </VStack>
                                  </Box>
                                )}
                              </VStack>
                            </CardBody>
                          </AccordionPanel>
                        </Card>
                      </AccordionItem>
                    </Accordion>
                  )}

                  {/* Analysis Summary Card (SA Specific) */}
                  {!initialLoading && aiAnalysis && (
                    <Card borderLeft="4px solid" borderColor={aiAnalysis.score >= 8 ? "green.400" : aiAnalysis.score >= 6 ? "yellow.400" : "red.400"} mb={6}>
                      <CardHeader pb={2}>
                        <HStack justify="space-between">
                          <Heading size="md" color="gray.700">Final QA Review</Heading>
                          {aiAnalysis.score !== null && aiAnalysis.score !== undefined && (
                            <Badge 
                              colorScheme={aiAnalysis.score >= 8 ? "green" : aiAnalysis.score >= 6 ? "yellow" : "red"} 
                              fontSize="md" 
                              p={2} 
                              borderRadius="md"
                            >
                              Confidence Score: {aiAnalysis.score}/10
                            </Badge>
                          )}
                        </HStack>
                      </CardHeader>
                      {/* <CardBody pt={0}>
                        {aiAnalysis.notes && (
                          <Text fontSize="sm" color="gray.800" whiteSpace="pre-wrap">
                            {aiAnalysis.notes}
                          </Text>
                        )}
                      </CardBody> */}
                    </Card>
                  )}

                  {/* Summary Cards (Coverage, MAC, Context) */}
                  {/* <HStack spacing={6} align="stretch" mb={4}>
                    <Card flex={1}>
                      <CardHeader pb={2}>
                        <Heading size="sm" color="gray.500" textTransform="uppercase">Coverage Basis</Heading>
                      </CardHeader>
                      <CardBody pt={0}>
                        <Text fontSize="2xl" fontWeight="bold">{results.coverage_basis}</Text>
                        <Text fontSize="sm" color="gray.500" mt={1}>
                          Policies Checked: {results.policy_ids_checked?.join(', ') || 'None'}
                        </Text>
                      </CardBody>
                    </Card>

                    <Card flex={1}>
                      <CardHeader pb={2}>
                        <Heading size="sm" color="gray.500" textTransform="uppercase">MAC Jurisdiction</Heading>
                      </CardHeader>
                      <CardBody pt={0}>
                        <Text fontSize="lg" fontWeight="medium">{results.mac_jurisdiction}</Text>
                        <Text fontSize="sm" color="gray.500" mt={1}>
                          Service Date: {results.service_date || 'Unknown'}
                        </Text>
                      </CardBody>
                    </Card>

                    <Card flex={1}>
                      <CardHeader pb={2}>
                        <Heading size="sm" color="gray.500" textTransform="uppercase">Case Context</Heading>
                      </CardHeader>
                      <CardBody pt={0}>
                        <Text fontSize="sm" fontWeight="bold">CPTs: <Badge colorScheme="purple">{results.context?.cpt_codes?.join(', ') || 'None'}</Badge></Text>
                        <Text fontSize="sm" mt={1}>Payer: {results.context?.payer}</Text>
                        <Text fontSize="sm">Carrier: {results.context?.carrier}</Text>
                        <Text fontSize="sm">State: {results.context?.state}</Text>
                      </CardBody>
                    </Card>
                  </HStack> */}

                  {/* LCD Relevancy Scoring (Evaluation Section) */}
                  {results.lcd_relevancy_analysis && (
                    <Accordion allowToggle>
                      <AccordionItem border="none">
                        <Card borderLeft="4px solid" borderColor="purple.400">
                          <CardHeader p={0}>
                            <AccordionButton _hover={{ bg: 'purple.50' }} p={4}>
                              <Box flex="1" textAlign="left">
                                <Heading size="md" color="purple.700">LCD Relevancy Evaluation (Beta)</Heading>
                                <Text fontSize="sm" color="gray.600" mt={1}>
                                  AI-powered assessment of which candidate LCDs are most relevant to the clinical documentation.
                                </Text>
                              </Box>
                              <AccordionIcon />
                            </AccordionButton>
                          </CardHeader>
                          <AccordionPanel pb={4}>
                            <CardBody pt={0}>
                              <VStack align="stretch" spacing={4}>
                                {results.lcd_relevancy_analysis.discussion && (
                                  <Box p={3} bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.200">
                                    <Text fontSize="sm" fontWeight="semibold" mb={1}>Clinical Context Analysis:</Text>
                                    <Text fontSize="sm">{results.lcd_relevancy_analysis.discussion}</Text>
                                  </Box>
                                )}
                                
                                {results.lcd_relevancy_analysis.relevancy_scores && results.lcd_relevancy_analysis.relevancy_scores.length > 0 && (
                                  <Box>
                                    <Text fontSize="sm" fontWeight="semibold" mb={2}>Candidate Policy Scores:</Text>
                                    <Table variant="simple" size="sm">
                                      <Thead>
                                        <Tr>
                                          <Th>Policy ID</Th>
                                          <Th>Relevancy Score</Th>
                                          <Th>Reason</Th>
                                        </Tr>
                                      </Thead>
                                      <Tbody>
                                        {results.lcd_relevancy_analysis.relevancy_scores
                                          .sort((a, b) => b.score - a.score)
                                          .map((scoreItem, idx) => (
                                          <Tr key={idx}>
                                            <Td fontWeight="bold" color="blue.600">{scoreItem.policy_id || scoreItem.lcd_id}</Td>
                                            <Td>
                                              <Badge colorScheme={scoreItem.score >= 80 ? 'green' : scoreItem.score >= 40 ? 'yellow' : 'red'}>
                                                {scoreItem.score}/100
                                              </Badge>
                                            </Td>
                                            <Td fontSize="sm" whiteSpace="normal">{scoreItem.reason}</Td>
                                          </Tr>
                                        ))}
                                      </Tbody>
                                    </Table>
                                  </Box>
                                )}
                              </VStack>
                            </CardBody>
                          </AccordionPanel>
                        </Card>
                      </AccordionItem>
                    </Accordion>
                  )}

                  {/* Excludes Analysis */}
                  {results.excludes_analysis && (
                    <Accordion allowToggle>
                      <AccordionItem border="none">
                        <Card borderLeft="4px solid" borderColor={results.excludes_analysis.conflicts_found?.length > 0 ? "orange.400" : "green.400"}>
                          <CardHeader p={0}>
                            <AccordionButton _hover={{ bg: results.excludes_analysis.conflicts_found?.length > 0 ? 'orange.50' : 'green.50' }} p={4}>
                              <Box flex="1" textAlign="left">
                                <Heading size="md" display="flex" alignItems="center">
                                  {results.excludes_analysis.conflicts_found?.length > 0 ? (
                                    <WarningIcon color="orange.400" mr={2} />
                                  ) : (
                                    <CheckCircleIcon color="green.400" mr={2} />
                                  )}
                                  CDC Excludes Conflict Resolution
                                </Heading>
                              </Box>
                              <AccordionIcon />
                            </AccordionButton>
                          </CardHeader>
                          <AccordionPanel pb={4}>
                            <CardBody pt={0}>
                              {results.excludes_analysis.conflicts_found?.length > 0 ? (
                                <>
                                  <Text fontSize="sm" mb={4}>
                                    The pipeline detected CDC coding conflicts among the extracted diagnoses. An AI review was performed to resolve them based on the clinical documentation.
                                  </Text>
                                  
                                  <HStack align="stretch" spacing={6}>
                                    <Box flex={1}>
                                      <Text fontWeight="bold" fontSize="sm" mb={2}>Conflicts Detected:</Text>
                                      <VStack align="stretch" spacing={2}>
                                        {results.excludes_analysis.conflicts_found.map((conflict, idx) => (
                                          <Box key={idx} bg="gray.50" p={2} borderRadius="md">
                                            <Badge colorScheme={conflict.type === 'Excludes1' ? 'red' : 'orange'} mr={2}>
                                              {conflict.type}
                                            </Badge>
                                            <Text as="span" fontWeight="bold">{conflict.code1}</Text> and <Text as="span" fontWeight="bold">{conflict.code2}</Text>
                                            <Text fontSize="xs" color="gray.600" mt={1}>{conflict.description}</Text>
                                          </Box>
                                        ))}
                                      </VStack>
                                    </Box>
                                    
                                    <Box flex={1}>
                                      <Text fontWeight="bold" fontSize="sm" mb={2}>AI Resolution:</Text>
                                      <Box bg="blue.50" p={3} borderRadius="md" height="100%">
                                        <HStack mb={2}>
                                          <Badge colorScheme="green">Kept: {results.excludes_analysis.ai_resolution?.keep?.join(', ') || 'None'}</Badge>
                                          <Badge colorScheme="red">Dropped: {results.excludes_analysis.ai_resolution?.drop?.join(', ') || 'None'}</Badge>
                                        </HStack>
                                        <Text fontSize="sm" fontStyle="italic">
                                          "{results.excludes_analysis.ai_resolution?.rationale}"
                                        </Text>
                                      </Box>
                                    </Box>
                                  </HStack>
                                </>
                              ) : (
                                <Text fontSize="sm" color="gray.600">
                                  No CDC Excludes1 or Excludes2 conflicts were detected among the extracted diagnoses.
                                </Text>
                              )}
                            </CardBody>
                          </AccordionPanel>
                        </Card>
                      </AccordionItem>
                    </Accordion>
                  )}

                  {/* Warnings & Rationale */}
                  <HStack spacing={6} align="stretch">
                    <Accordion allowToggle flex={1}>
                      <AccordionItem border="none">
                        <Card h="100%">
                          <CardHeader p={0}>
                            <AccordionButton _hover={{ bg: 'orange.50' }} p={4}>
                              <Box flex="1" textAlign="left">
                                <Heading size="md" display="flex" alignItems="center">
                                  <WarningIcon color="orange.400" mr={2} /> Warnings
                                </Heading>
                              </Box>
                              <AccordionIcon />
                            </AccordionButton>
                          </CardHeader>
                          <AccordionPanel pb={4}>
                            <CardBody pt={0}>
                              {results.warnings && results.warnings.length > 0 ? (
                                <VStack align="start" spacing={2}>
                                  {results.warnings.map((warning, idx) => (
                                    <HStack key={idx} align="start">
                                      <WarningIcon color="orange.400" mt={1} />
                                      <Text fontSize="sm">{warning}</Text>
                                    </HStack>
                                  ))}
                                </VStack>
                              ) : (
                                <Text color="gray.500" fontSize="sm">No warnings generated.</Text>
                              )}
                            </CardBody>
                          </AccordionPanel>
                        </Card>
                      </AccordionItem>
                    </Accordion>

                    <Accordion allowToggle flex={1}>
                      <AccordionItem border="none">
                        <Card h="100%">
                          <CardHeader p={0}>
                            <AccordionButton _hover={{ bg: 'blue.50' }} p={4}>
                              <Box flex="1" textAlign="left">
                                <Heading size="md" display="flex" alignItems="center">
                                  <InfoIcon color="blue.400" mr={2} /> Pipeline Rationale
                                </Heading>
                              </Box>
                              <AccordionIcon />
                            </AccordionButton>
                          </CardHeader>
                          <AccordionPanel pb={4}>
                            <CardBody pt={0}>
                              {results.rationale && results.rationale.length > 0 ? (
                                <VStack align="start" spacing={2}>
                                  {results.rationale.map((rat, idx) => (
                                    <HStack key={idx} align="start">
                                      <CheckCircleIcon color="green.400" mt={1} />
                                      <Text fontSize="sm">{rat}</Text>
                                    </HStack>
                                  ))}
                                </VStack>
                              ) : (
                                <Text color="gray.500" fontSize="sm">No rationale provided.</Text>
                              )}
                            </CardBody>
                          </AccordionPanel>
                        </Card>
                      </AccordionItem>
                    </Accordion>
                  </HStack>

                </VStack>
              )}

              {!initialLoading && (!claimsData || !claimsData[claimType]) && (
                <Alert status="info" mt={4} borderRadius="md">
                  <AlertIcon />
                  No CPT coding results found for this case.
                </Alert>
              )}
            </TabPanel>

            <TabPanel px={0} py={4}>
              <Card>
                <CardHeader>
                  <Heading size="md">Coding Assistant</Heading>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    Ask questions about the coding rules and logic applied to this case.
                  </Text>
                </CardHeader>
                <CardBody>
                  <ModernChat 
                    ref={chatRef}
                    caseId={caseId} 
                    contextType="mph_surgical_assist" 
                    height="600px"
                    additionalContext={claimsData ? JSON.stringify(claimsData, null, 2) : null}
                  />
                </CardBody>
              </Card>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {/* Document Viewer */}
      <Box mt={10}>
        <VarianceDocumentViewer 
          caseId={caseId} 
          caseFiles={caseFiles} 
          accessToken={accessToken} 
        />
      </Box>
    </Box>
  );
};

export default MPHSurgicalAssistAnalysis;
